import type { GenerateBuildingFeatureSet } from "building-types/building/buildingTypes";
import type { IsolatableEntityHandle } from "building-types/handles/docEntityHandleTypes";
import uid from "common/utils/uid";
import { PlanSetComputeServiceBase } from "lib/house/plans/planSetComputeServiceBase";
import type { DocDelta } from "lib/studio/docDelta";
import type {
  PlanSetClearHeavyCachesMessage,
  PlanSetJobCompleteResponse,
  PlanSetJobErrorResponse,
  PlanSetRequestPayload,
  PlanSetResponsePayload,
} from "lib/workers/planSetComputeWorkerMessages";

/**
 * Wraps background thread creation of plan geometry for a viewport. It makes it practical and efficient to generate
 * plan geometry or building faces by caching the output and ensuring only fragments of the document are transferred to the worker.
 */
export class PlanSetComputeService extends PlanSetComputeServiceBase {
  private static _instance: PlanSetComputeService;
  private onError?: (msg: Error) => void;
  private static worker: Worker;

  private static createWorker(): Worker {
    return new Worker(new URL("../../../workers/planSetCompute.worker.ts", import.meta.url), {
      type: "module",
    });
  }

  private constructor(onError?: (msg: Error) => void) {
    super();
    if (!PlanSetComputeService.worker) {
      PlanSetComputeService.worker = PlanSetComputeService.createWorker();
    }
    this.onError = onError;
  }

  static getInstance(onError?: (msg: Error) => void) {
    if (!this._instance) {
      this._instance = new PlanSetComputeService(onError);
    } else if (onError && this._instance.onError !== onError) {
      this._instance.onError = onError;
    }
    return this._instance;
  }

  /**
   * Drop leftover plan-set caches after PDF/DXF export so a later export cannot
   * OOM. Recreates the worker (or CLEAR_HEAVY_CACHES if jobs are in flight). The
   * Studio viewport rebuilds plan geometry on the next interaction.
   */
  static releaseExportMemory(): void {
    PlanSetComputeServiceBase.clearCache();
    if (!PlanSetComputeService._instance) {
      return;
    }
    PlanSetComputeService._instance.resetDocSync();
    if (PlanSetComputeService._pendingJobCount > 0) {
      PlanSetComputeService.terminateWhenIdle = true;
      PlanSetComputeService.worker.postMessage({ type: "CLEAR_HEAVY_CACHES" } satisfies PlanSetClearHeavyCachesMessage);
      return;
    }
    PlanSetComputeService.replaceWorker();
  }

  private static terminateWhenIdle = false;

  private static replaceWorker(): void {
    // Killing the worker frees BSP/WASM with it; we do not call dispose() first.
    if (PlanSetComputeService.worker) {
      PlanSetComputeService.worker.terminate();
    }
    PlanSetComputeService.worker = PlanSetComputeService.createWorker();
    PlanSetComputeService._pendingJobCount = 0;
    PlanSetComputeService.terminateWhenIdle = false;
    PlanSetComputeService.notifyJobStatusChanged();
  }

  static _pendingJobCount = 0;
  public get pendingJobCount() {
    return PlanSetComputeService._pendingJobCount;
  }

  static jobStatusSubscribers: (() => void)[] = [];

  onJobStatusChanged(callback: () => void): () => void {
    PlanSetComputeService.jobStatusSubscribers.push(callback);
    return () => {
      PlanSetComputeService.jobStatusSubscribers = PlanSetComputeService.jobStatusSubscribers.filter(
        (cb) => cb !== callback,
      );
    };
  }

  static notifyJobStatusChanged() {
    for (const cb of PlanSetComputeService.jobStatusSubscribers) {
      cb();
    }
  }

  private addMessageListener(listener: (this: Worker, ev: MessageEvent<any>) => any) {
    PlanSetComputeService._pendingJobCount++;
    PlanSetComputeService.worker.addEventListener("message", listener);
    PlanSetComputeService.notifyJobStatusChanged();
  }

  private removeMessageListener(listener: (this: Worker, ev: MessageEvent<any>) => any) {
    PlanSetComputeService._pendingJobCount--;
    PlanSetComputeService.worker.removeEventListener("message", listener);
    PlanSetComputeService.notifyJobStatusChanged();
    if (PlanSetComputeService.terminateWhenIdle && PlanSetComputeService._pendingJobCount <= 0) {
      queueMicrotask(() => {
        if (PlanSetComputeService.terminateWhenIdle && PlanSetComputeService._pendingJobCount <= 0) {
          PlanSetComputeService.replaceWorker();
        }
      });
    }
  }

  executeJob(
    payload: PlanSetRequestPayload,
    docDelta: DocDelta,
    enabledFeatures?: GenerateBuildingFeatureSet,
    isolatedEntity?: IsolatableEntityHandle,
  ): Promise<PlanSetResponsePayload>[] {
    const jobId = uid();

    const responses: Promise<PlanSetResponsePayload>[] = [];
    const resolvers: { resolve: (value: PlanSetResponsePayload) => void; reject: (error: Error) => void }[] = [];

    const numberOfResponses = PlanSetComputeServiceBase.planSetJobNumberOfPromises(payload);
    let numberOfOutstandingResponses = numberOfResponses;

    const responseReceived = (msg: MessageEvent<PlanSetJobCompleteResponse | PlanSetJobErrorResponse>) => {
      const jobEvent = msg.data;
      if (jobId !== jobEvent.id) {
        return;
      }
      numberOfOutstandingResponses--;
      if (numberOfOutstandingResponses <= 0) {
        this.removeMessageListener(responseReceived);
        PlanSetComputeService.notifyJobStatusChanged();
      }
      if (jobEvent.type === "PLAN_SET_JOB_COMPLETE") {
        const resolveNextPromise = resolvers.shift()?.resolve;
        if (resolveNextPromise) {
          resolveNextPromise(jobEvent.response);
        }
        return;
      } else if (jobEvent.type === "PLAN_SET_JOB_ERROR") {
        const error = jobEvent.error;
        while (resolvers.length) {
          const rejectNextPromise = resolvers.shift()?.reject;
          if (rejectNextPromise) {
            rejectNextPromise(error);
          }
        }
        this.removeMessageListener(responseReceived);
        PlanSetComputeService.notifyJobStatusChanged();
        return;
      }
    };

    for (let i = 0; i < numberOfResponses; i++) {
      let resolve, reject;
      responses.push(
        new Promise<PlanSetResponsePayload>((res, rej) => {
          resolve = res;
          reject = rej;
        }),
      );
      void responses[i].catch(() => undefined);
      resolvers.push({ resolve, reject });
    }
    this.addMessageListener(responseReceived);

    // we send only the doc diff to the worker
    PlanSetComputeService.worker.postMessage({
      id: jobId,
      docDelta,
      payload,
      enabledFeatures,
      isolatedEntity,
    });

    return responses;
  }
}
